import { useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useToast } from './useToast';

// Keyboard shortcut configuration
export interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
  metaKey?: boolean;
  description: string;
  category: string;
  action: (event?: KeyboardEvent) => void;
  disabled?: boolean;
  /** Listed in the help modal only — the dispatcher must never match (and so
   *  never preventDefault) these, or native browser behaviour is destroyed. */
  displayOnly?: boolean;
}

export interface ShortcutCategory {
  name: string;
  shortcuts: KeyboardShortcut[];
}

// Hook for managing keyboard shortcuts
export const useKeyboardShortcuts = () => {
  const navigate = useNavigate();
  const { toggleTheme } = useTheme();
  const toast = useToast();
  const shortcutsRef = useRef<KeyboardShortcut[]>([]);
  const isModalOpenRef = useRef(false);

  // Set modal state (to disable shortcuts when modals are open)
  const setModalOpen = useCallback((isOpen: boolean) => {
    isModalOpenRef.current = isOpen;
  }, []);

  // Show keyboard shortcuts help
  const showShortcutsHelp = useCallback(() => {
    // This will be handled by the ShortcutsModal component
    const event = new CustomEvent('show-shortcuts-modal');
    window.dispatchEvent(event);
  }, []);

  // Define all keyboard shortcuts
  const shortcuts: KeyboardShortcut[] = [
    // Navigation shortcuts
    {
      key: 'h',
      altKey: true,
      description: 'Go to Dashboard',
      category: 'Navigation',
      action: () => navigate('/'),
    },
    {
      key: 'p',
      altKey: true,
      description: 'Go to Projects',
      category: 'Navigation',
      action: () => navigate('/projects'),
    },
    {
      key: 's',
      altKey: true,
      description: 'Go to SEO Analysis',
      category: 'Navigation',
      action: () => navigate('/seo-analysis'),
    },
    {
      key: 'c',
      altKey: true,
      description: 'Go to Competitor Analysis',
      category: 'Navigation',
      action: () => navigate('/competitor-analysis'),
    },
    {
      key: 'k',
      altKey: true,
      description: 'Go to Keyword Research',
      category: 'Navigation',
      action: () => navigate('/keyword-research'),
    },
    {
      key: 'g',
      altKey: true,
      description: 'Go to Geographic Analytics',
      category: 'Navigation',
      action: () => navigate('/geographic-analytics'),
    },
    {
      key: 'a',
      altKey: true,
      description: 'Go to A/B Testing',
      category: 'Navigation',
      action: () => navigate('/ab-testing'),
    },
    {
      key: 'n',
      altKey: true,
      description: 'Go to Knowledge Graph',
      category: 'Navigation',
      action: () => navigate('/knowledge-graph'),
    },
    {
      key: 'u',
      altKey: true,
      description: 'Go to Account Settings',
      category: 'Navigation',
      action: () => navigate('/settings'),
    },

    // Global actions
    {
      key: 't',
      ctrlKey: true,
      shiftKey: true,
      description: 'Toggle Dark Mode',
      category: 'Global',
      action: () => {
        toggleTheme();
        toast.success('Theme toggled');
      },
    },
    {
      key: '?',
      description: 'Show Keyboard Shortcuts',
      category: 'Global',
      action: showShortcutsHelp,
    },
    {
      key: 'Escape',
      description: 'Close Modal/Dialog',
      category: 'Global',
      action: () => {
        // Close any open modals
        const event = new CustomEvent('close-modal');
        window.dispatchEvent(event);
      },
    },

    // Search shortcuts
    {
      key: 'f',
      ctrlKey: true,
      description: 'Focus Search',
      category: 'Search',
      action: () => {
        const searchInput = document.querySelector('input[placeholder*="Search"]') as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
          searchInput.select();
        }
      },
    },
    {
      key: 'k',
      ctrlKey: true,
      description: 'Quick Search',
      category: 'Search',
      action: () => {
        // Focus the main search input in header
        const headerSearch = document.querySelector('header input[type="text"]') as HTMLInputElement;
        if (headerSearch) {
          headerSearch.focus();
          headerSearch.select();
        }
      },
    },

    // Analysis shortcuts
    {
      key: 'r',
      ctrlKey: true,
      shiftKey: true,
      description: 'Run SEO Analysis',
      category: 'Analysis',
      action: () => {
        const runButton = document.querySelector('button[data-action="run-analysis"]') as HTMLButtonElement;
        if (runButton && !runButton.disabled) {
          runButton.click();
        }
      },
    },
    {
      key: 'e',
      ctrlKey: true,
      shiftKey: true,
      description: 'Export Current Data',
      category: 'Analysis',
      action: () => {
        const exportButton = document.querySelector('button[data-action="export"]') as HTMLButtonElement;
        if (exportButton) {
          exportButton.click();
        }
      },
    },

    // Data management
    {
      key: 'n',
      ctrlKey: true,
      description: 'Create New Item',
      category: 'Data',
      action: () => {
        const newButton = document.querySelector('button[data-action="create-new"]') as HTMLButtonElement;
        if (newButton) {
          newButton.click();
        }
      },
    },
    {
      key: 's',
      ctrlKey: true,
      description: 'Save Current Form',
      category: 'Data',
      action: (event) => {
        event?.preventDefault();
        const saveButton = document.querySelector('button[type="submit"], button[data-action="save"]') as HTMLButtonElement;
        if (saveButton && !saveButton.disabled) {
          saveButton.click();
        }
      },
    },

    // Widget actions
    {
      key: 'r',
      altKey: true,
      description: 'Refresh All Widgets',
      category: 'Widgets',
      action: () => {
        const refreshButtons = document.querySelectorAll('button[aria-label*="Refresh"], button[title*="Refresh"]');
        refreshButtons.forEach((button) => {
          if (button instanceof HTMLButtonElement && !button.disabled) {
            button.click();
          }
        });
        toast.info('Refreshing all widgets...');
      },
    },

    // Accessibility shortcuts — displayOnly: the browser provides these
    // natively; matching them in the dispatcher would preventDefault and
    // destroy native Tab/Enter behaviour wherever focus is on a
    // non-input element (buttons, the consent banner's shadow host, …).
    {
      key: 'Tab',
      shiftKey: true,
      description: 'Previous Focusable Element',
      category: 'Accessibility',
      displayOnly: true,
      action: () => {
        // Browser handles this natively, but we track it for help
      },
    },
    {
      key: 'Tab',
      description: 'Next Focusable Element',
      category: 'Accessibility',
      displayOnly: true,
      action: () => {
        // Browser handles this natively, but we track it for help
      },
    },
    {
      key: 'Enter',
      description: 'Activate Focused Element',
      category: 'Accessibility',
      displayOnly: true,
      action: () => {
        // Browser handles this natively, but we track it for help
      },
    },
  ];

  // Store shortcuts in ref for access
  shortcutsRef.current = shortcuts;

  // Handle keyboard events
  const handleKeyDown = useCallback((event: KeyboardEvent) => {
    // Don't trigger shortcuts when typing in inputs or when modals are open
    if (
      isModalOpenRef.current ||
      event.target instanceof HTMLInputElement ||
      event.target instanceof HTMLTextAreaElement ||
      event.target instanceof HTMLSelectElement ||
      (event.target as HTMLElement)?.contentEditable === 'true'
    ) {
      // Allow Escape to close modals even when typing
      if (event.key === 'Escape' && isModalOpenRef.current) {
        const escapeShortcut = shortcuts.find(s => s.key === 'Escape');
        if (escapeShortcut) {
          escapeShortcut.action();
        }
      }
      return;
    }

    // Find matching shortcut
    const matchingShortcut = shortcuts.find(shortcut => {
      if (shortcut.disabled || shortcut.displayOnly) return false;
      
      return (
        shortcut.key.toLowerCase() === event.key.toLowerCase() &&
        !!shortcut.ctrlKey === event.ctrlKey &&
        !!shortcut.altKey === event.altKey &&
        !!shortcut.shiftKey === event.shiftKey &&
        !!shortcut.metaKey === event.metaKey
      );
    });

    if (matchingShortcut) {
      event.preventDefault();
      event.stopPropagation();
      
      try {
        matchingShortcut.action(event);
      } catch (error) {
        console.error('Error executing keyboard shortcut:', error);
        toast.error('Error executing shortcut');
      }
    }
  }, [shortcuts, toast]);

  // Set up event listeners
  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  // Group shortcuts by category
  const getShortcutsByCategory = useCallback((): ShortcutCategory[] => {
    const categories: { [key: string]: KeyboardShortcut[] } = {};
    
    shortcuts.forEach(shortcut => {
      if (!categories[shortcut.category]) {
        categories[shortcut.category] = [];
      }
      categories[shortcut.category].push(shortcut);
    });

    return Object.entries(categories).map(([name, shortcuts]) => ({
      name,
      shortcuts: shortcuts.sort((a, b) => a.description.localeCompare(b.description))
    }));
  }, [shortcuts]);

  // Format shortcut key combination for display
  const formatShortcut = useCallback((shortcut: KeyboardShortcut): string => {
    const parts: string[] = [];
    
    if (shortcut.ctrlKey) parts.push('Ctrl');
    if (shortcut.altKey) parts.push('Alt');
    if (shortcut.shiftKey) parts.push('Shift');
    if (shortcut.metaKey) parts.push('Cmd');
    
    // Handle special keys
    const keyDisplay = shortcut.key === ' ' ? 'Space' : 
                      shortcut.key === 'Escape' ? 'Esc' :
                      shortcut.key === '?' ? '?' :
                      shortcut.key.toUpperCase();
    
    parts.push(keyDisplay);
    
    return parts.join(' + ');
  }, []);

  return {
    shortcuts: shortcutsRef.current,
    getShortcutsByCategory,
    formatShortcut,
    setModalOpen,
    showShortcutsHelp,
  };
};

// Hook for individual components to register shortcuts
export const useComponentShortcuts = (componentShortcuts: KeyboardShortcut[]) => {
  const { setModalOpen } = useKeyboardShortcuts();
  
  useEffect(() => {
    // Component shortcuts are handled by the main hook
    // This hook is for component-specific setup if needed
    return () => {
      // Cleanup if needed
    };
  }, [componentShortcuts, setModalOpen]);
};

export default useKeyboardShortcuts;